/**
 * Keyboard → `FlightInputs` (packages/engine). Pure: a set of currently-held
 * key names in, one tick's sim inputs out — no DOM listeners, no module
 * state, so the whole mapping is unit-testable including simultaneous axes.
 *
 * The bindings follow the original's spirit rather than its literal key map:
 * `docs/game/ui-menus.md`'s Open Questions records that the exact
 * keyboard/joystick bindings are **not** recoverable from the binary string
 * dumps (they live in `CONTROL.PRF`/binary key-map data), so only the ones
 * the manual actually names in prose are ground truth — the **U key** to
 * unjam guns (`docs/game/flight-model.md` §4). Everything else here is a
 * port-side choice, kept conventional for a keyboard flight sim: arrows are
 * the stick, letters either side of the home row are the rudder bar, and
 * `W`/`S` plus `Space` are modern left-hand seconds for the pitch axis and
 * the trigger (user request, 2026-08-19) — additive, never replacing the
 * arrow/`F` bindings.
 */

import { clamp, NEUTRAL_INPUTS, type FlightInputs, type StickAxes } from "@red-opus/engine";
// AD-021's hold guard lives in its own file — the port's one timed-hold key,
// and the shape does not fit either of this file's two (see `bail-key.ts`).
// The *binding* half stays here: `FLIGHT_KEYS` and `CONTROL_BINDINGS`.
import { BAIL_OUT_KEY } from "./bail-key.ts";

export type { ControlBinding, ControlId } from "./control-bindings.ts";
export { CONTROL_BINDINGS } from "./control-bindings.ts";

/**
 * Normalizes a `KeyboardEvent.key` to the literals the tables below use:
 * arrows keep their `Arrow…` names, everything else folds to lower case so
 * a held Shift (needed for `+` on most layouts) cannot change the binding.
 */
export function normalizeKey(key: string): string {
  return key.startsWith("Arrow") ? key : key.toLowerCase();
}

/** Keys that must not also scroll/act on the page while flying. */
export function isFlightKey(key: string): boolean {
  return FLIGHT_KEYS.has(normalizeKey(key));
}

interface AxisBinding {
  readonly negative: readonly string[];
  readonly positive: readonly string[];
}

/**
 * `W`/`S` are a second seat for the pitch axis alongside the arrows (user
 * request, 2026-08-19), bound to the *same* sense the arrows already use —
 * `ArrowUp` is stick *forward* (nose down, negative pitch), so `W` is too,
 * and `S` joins `ArrowDown` as back stick. They share one `AxisBinding`
 * rather than getting their own, which is what makes `W`+`ArrowDown`
 * cancel exactly like `ArrowUp`+`ArrowDown` does.
 */
const PITCH: AxisBinding = { negative: ["ArrowUp", "w"], positive: ["ArrowDown", "s"] };
const ROLL: AxisBinding = { negative: ["ArrowLeft", "a"], positive: ["ArrowRight", "d"] };
const YAW: AxisBinding = { negative: ["z", ","], positive: ["x", "."] };
const THROTTLE: AxisBinding = { negative: ["-", "_"], positive: ["+", "="] };

/** `" "` is `KeyboardEvent.key` for the space bar, which `normalizeKey` leaves untouched (`" ".toLowerCase() === " "`). */
const FIRE_KEYS: readonly string[] = ["f", " "];
const CLEAR_JAM_KEY = "u";

/**
 * The bomb toggle. **`B` is free in the original and free here** — the Dynamix
 * Quick Reference Card's key list (transcribed in
 * docs/game/player-bombing.md § *The original's key map*) has no bomb command at
 * all, because the original never gives the player anything to drop. So this is
 * a port-added binding for a port-added capability, and `B` is the one letter
 * that collides with nothing in either map.
 *
 * Held rather than one-shot, like `firing`: the engine's rack spaces the stick
 * by `bombReleaseIntervalSeconds` (`combat/bomb-pass.ts`), so holding it walks
 * the bombs off at a bomber's own cadence and a tap drops exactly one.
 */
const BOMB_RELEASE_KEY = "b";

/**
 * `K` for time compression — a port choice, like every other binding in this
 * file the manual/string dumps don't name a key for (`time-compression.ts`'s
 * header has the full account of what *is* and isn't recoverable about this
 * feature). One-shot: each press toggles the request, and `flight-scene.ts`
 * resolves whether it's actually allowed to engage.
 */
const TIME_COMPRESS_KEY = "k";

/**
 * `M` opens the map, and mute moves to `V`.
 *
 * **The original's card settles both halves of that swap.** Its key list
 * (transcribed in docs/game/player-bombing.md § *The original's key map*) has
 * no map command at all — the maps were five fold-out sheets in the box, and
 * `RB.EXE` carries no map module, resource or key (see
 * docs/engineering/in-flight-map.md). So `M` is not being taken *from* a map
 * key; the port is adding one, and `M` is the letter a player reaches for.
 * `M` is not free in the original either — it is FLIGHT LEADER COMMANDS'
 * *minor wing attack* — but this port has no flight-leader command set to
 * collide with, and the map is the more useful thing to have on the obvious
 * letter.
 *
 * Mute's own authority is `Alt-S` (sound on/off) and `Alt-M` (music on/off) on
 * the card, both unusable as bare bindings here for the same reason the
 * cockpit's `F1`–`F6` are: browser chrome. `V` is free in the original's list
 * *and* unused by this port, and "V for volume" is the one mnemonic left after
 * `S` went to the pitch axis and `M` to the map.
 */
const MAP_KEY = "m";
const MUTE_KEY = "v";

/**
 * `O` opens the PREFERENCES panel — one key for every setting this port has
 * (`shell/model/preferences-screen.ts`), replacing the row of HTML chips that
 * used to carry them a click each.
 *
 * A port binding for a port screen, on the same footing as `M`. The original's
 * Quick Reference Card (transcribed in docs/game/player-bombing.md § *The
 * original's key map*) has no in-flight options key at all — `RB.EXE`'s
 * settings live on the pre-flight Realism Panel and in `PS.EXE`'s PREFERENCES,
 * neither of which a cockpit can reach — and `O` is free in that list *and*
 * unused here. It is also the letter a player reaches for: `P` is pause and
 * `S` is the pitch axis, so "O for options" is what is left.
 *
 * Exported because `flight/PreferencesOverlay.tsx` closes on the same key it
 * opened with, and the panel swallows the keyboard while it is up — so the
 * closing press never reaches `discreteCommandFor` and the two would otherwise
 * be two independent spellings of one binding.
 */
export const PREFERENCES_KEY = "o";

/**
 * `Escape` — the original's own in-flight menu key (G38), and the one binding in
 * this table that is not a letter.
 *
 * **Lower-case on purpose.** `normalizeKey` passes `Arrow*` through and
 * lower-cases everything else, so a `DISCRETE_COMMANDS` entry keyed `"Escape"`
 * would never be found. Being in that table also puts it in `FLIGHT_KEYS`, which
 * is what stops the key reaching the page — note the side effect that while
 * `/fly` is mounted `Escape` no longer leaves browser fullscreen, which is the
 * price of the original's own binding and is the same trade every other flight
 * key makes.
 */
export const ESC_MENU_KEY = "escape";

const DISCRETE_COMMANDS: Readonly<Record<string, DiscreteCommand>> = {
  "1": "cameraChase",
  "2": "cameraFlyBy",
  "3": "cameraCockpit",
  c: "cameraCycle",
  p: "pause",
  [MAP_KEY]: "map",
  [MUTE_KEY]: "muteAudio",
  [TIME_COMPRESS_KEY]: "timeCompress",
  [PREFERENCES_KEY]: "preferences",
  [ESC_MENU_KEY]: "escMenu",
};

/**
 * Cockpit look-around: held, not one-shot (`keys.test.ts` covers
 * `glanceDirectionFor` below) — the fixed snap views `docs/game/manual`
 * documents for the VCR's cockpit playback ("COCKPIT allows you to look out
 * the forward, left, right, and back cockpit views", `04-gameplay.md:673`).
 *
 * The **up and down** glances are the original's too, and are now sourced: the
 * Quick Reference Card gives the cockpit a six-way snap — `F1` forward, `F2`
 * back, `F3` left, `F4` right, `F5` **up**, `F6` **down**
 * (docs/game/player-bombing.md § *The original's key map*). Down was the last
 * one missing here, and it is the whole aiming affordance a bombing pass gets:
 * WWI bomb-aiming from a scout was leaning over the side and judging it, so the
 * port gives the player that view and no pipper.
 *
 * Letters rather than the function keys the card lists, for the same reason
 * every other binding in this file is a letter: `F1`–`F6` are browser chrome.
 */
const LOOK_LEFT_KEY = "q";
const LOOK_RIGHT_KEY = "e";
const LOOK_BACK_KEY = "r";
const LOOK_UP_KEY = "t";
const LOOK_DOWN_KEY = "g";
const LOOK_KEYS: readonly string[] = [LOOK_LEFT_KEY, LOOK_RIGHT_KEY, LOOK_BACK_KEY, LOOK_UP_KEY, LOOK_DOWN_KEY];

const FLIGHT_KEYS: ReadonlySet<string> = new Set([
  ...PITCH.negative,
  ...PITCH.positive,
  ...ROLL.negative,
  ...ROLL.positive,
  ...YAW.negative,
  ...YAW.positive,
  ...THROTTLE.negative,
  ...THROTTLE.positive,
  ...FIRE_KEYS,
  CLEAR_JAM_KEY,
  BOMB_RELEASE_KEY,
  BAIL_OUT_KEY,
  ...LOOK_KEYS,
  ...Object.keys(DISCRETE_COMMANDS),
]);

export type DiscreteCommand =
  | "cameraChase"
  | "cameraFlyBy"
  | "cameraCockpit"
  | "cameraCycle"
  | "pause"
  | "muteAudio"
  | "timeCompress"
  | "map"
  | "preferences"
  | "escMenu";

/** One-shot commands, resolved on key *down* rather than from the held set. */
export function discreteCommandFor(key: string): DiscreteCommand | undefined {
  return DISCRETE_COMMANDS[normalizeKey(key)];
}

/** The original's six fixed cockpit snap views (§ its `F1`–`F6`; see `LOOK_LEFT_KEY`'s doc). */
export type GlanceDirection = "forward" | "left" | "right" | "back" | "up" | "down";

/** Priority when more than one glance key is held: back, then the two vertical extremes, then left/right. */
export function glanceDirectionFor(pressed: ReadonlySet<string>): GlanceDirection {
  if (pressed.has(LOOK_BACK_KEY)) return "back";
  if (pressed.has(LOOK_UP_KEY)) return "up";
  if (pressed.has(LOOK_DOWN_KEY)) return "down";
  if (pressed.has(LOOK_LEFT_KEY)) return "left";
  if (pressed.has(LOOK_RIGHT_KEY)) return "right";
  return "forward";
}

/** −1 / 0 / +1 for one axis; holding both directions cancels out, like a centred stick. */
function axisValue(pressed: ReadonlySet<string>, axis: AxisBinding): number {
  const negative = axis.negative.some((key) => pressed.has(key)) ? -1 : 0;
  const positive = axis.positive.some((key) => pressed.has(key)) ? 1 : 0;
  return negative + positive;
}

/** Full-travel time for the throttle lever, in seconds — a keyboard has no analogue axis. */
export const THROTTLE_TRAVEL_SECONDS = 2;

/**
 * `wheelDelta` is the mouse wheel's contribution for this frame
 * (`pointer.ts`), already sign-correct: it is summed with the key travel
 * before the single clamp, so pushing the wheel one way while holding the
 * opposite key nets out the way a real lever would.
 */
export function advanceThrottle(current: number, pressed: ReadonlySet<string>, dt: number, wheelDelta = 0): number {
  const direction = axisValue(pressed, THROTTLE);
  return clamp(current + (direction * dt) / THROTTLE_TRAVEL_SECONDS + wheelDelta, 0, 1);
}

/**
 * Throttle travel per pixel of wheel movement. A typical notch reports 100
 * px, which lands on 0.08 of full travel — the same distance a held `+` key
 * covers in 0.16 s, so the two feel like the same lever rather than two
 * different ones. Wheel *up* (negative `deltaY`, the browser's "scroll
 * towards the top of the document") is throttle up, matching a real quadrant.
 */
export const WHEEL_THROTTLE_PER_PIXEL = 1 / 1250;
/** `WheelEvent.deltaMode` unit conversions: 0 is already pixels, 1 is lines, 2 is pages. */
const WHEEL_PIXELS_PER_LINE = 16;
const WHEEL_PIXELS_PER_PAGE = 400;

export function throttleWheelDelta(deltaY: number, deltaMode = 0): number {
  if (!Number.isFinite(deltaY)) return 0;
  const scale = deltaMode === 1 ? WHEEL_PIXELS_PER_LINE : deltaMode === 2 ? WHEEL_PIXELS_PER_PAGE : 1;
  return -deltaY * scale * WHEEL_THROTTLE_PER_PIXEL;
}

/** The class the 3D viewport's host div carries (`FlightSurface.tsx`). */
export const FLIGHT_VIEWPORT_CLASS = "flight__canvas-host";

/**
 * Root classes of every piece of DOM chrome that sits over the viewport. A
 * pointer that lands inside one of these is operating the UI, not the
 * aeroplane. (`cockpit-panel` is on the list for completeness even though its
 * CSS already sets `pointer-events: none`, so it can never be a target.)
 */
export const UI_OVERLAY_CLASSES: readonly string[] = [
  "hud",
  "controls-hint",
  "debrief",
  "duel-outcome",
  // The Mission Recorder's three surfaces. A miss here is not cosmetic: a click
  // on the playback bar or the save prompt would fall through to the viewport
  // and pull the trigger (`isViewportPointerTarget`).
  "vcr",
  "tapes",
  "tape-save",
  // BEST MISSIONS' *"WHAT IS YOUR PILOT'S NAME?"* prompt, which is the same
  // shape as `tape-save` and was missing from this list — so a click on it
  // fell through and pulled the trigger. The third instance of this family;
  // the second is why `flight__settings-toggle` above is one button and not a
  // row of chips.
  "best-mission-save",
  "cockpit-panel",
  "flight__overlay",
  // The loading screen, which covers the whole view until the scene is ready.
  // Its own canvas is `flight-loading__canvas`, which the `root__` prefix rule
  // above already covers — the class is here rather than only in the CSS for the
  // reason `best-mission-save` records: a full-view surface that is not on this
  // list is a click that falls through to the viewport.
  "flight-loading",
  "flight__exit",
  "flight__saved-tape",
  // The one button in the corner: it opens the PREFERENCES panel, where every
  // setting lives. A row of per-setting chips is a standing hazard for this
  // list — each one needs its own entry, and a chip that is missing from it
  // **pulls the trigger** when it is clicked — so there is one button and one
  // panel instead.
  "flight__settings-toggle",
  // The PREFERENCES panel itself, and — through this list's `root__` prefix
  // rule — its canvas and its status line. It covers the whole view while it is
  // open, so without it a click on any control on it fires the guns behind it.
  "preferences-overlay",
  // The map sheet covers the whole view while it is open; without it here a
  // click on the paper falls through to the viewport and fires the guns.
  "map-overlay",
  // G38's in-flight `ESC` board. It needs this entry *more* than its three
  // siblings above, not less: it is the only one of the four that is
  // **transparent**, so the aeroplane really is visible behind it and a click on
  // RETURN TO BASE genuinely looks like a click on the sky. The element still
  // covers the whole view and still takes the event; this is what stops that
  // event also pulling the trigger.
  "esc-menu-overlay",
  // AD-015's kill-camera window. Its *body* still lets the pointer through —
  // the picture inside it is part of the 3D view, and firing through the corner
  // of the screen must go on working — but its title bar, its × and its resize
  // handle take pointer events, and this list is what stops a press on any of
  // the three pulling the trigger behind it. The layer the frame is positioned
  // in (`killcam-frame-layer`) needs no entry: it is `pointer-events: none` and
  // can never be a target, and anything inside it that can be has this class in
  // its chain.
  "killcam-frame",
  // `?boot-report`'s panel, on the same terms as the kill-camera window above:
  // the box is `pointer-events: none` and can never be a target, but its COPY
  // button takes `auto`, and the `root__` prefix rule reaches that button
  // through this entry. Without it, asking for the report would be pressing
  // the trigger.
  "flight__boot-report",
  // VIEW AIRPLANES' three effect chips (`planes/ViewerEffectsControls.tsx`)
  // share the flight row's classes. That route has no viewport to fall through
  // to, but the classes are exempt here so a chip cannot become a hazard by
  // being reused on a flying surface later.
  "flight__propeller-toggle",
  "flight__enhanced-effects-toggle",
  "flight__enhanced-models-toggle",
];

function isOverlayClass(name: string): boolean {
  return UI_OVERLAY_CLASSES.includes(name) || UI_OVERLAY_CLASSES.some((root) => name.startsWith(`${root}__`));
}

/**
 * Whether a pointer event whose target-to-root class chain is `classChain`
 * landed on the 3D viewport rather than on chrome over it. Walks outward from
 * the target and takes the first answer: an overlay class wins if it is
 * nearer the target than the viewport host, which is what makes a HUD button
 * *inside* the canvas host's bounding box still count as UI.
 */
export function isViewportPointerTarget(classChain: readonly string[]): boolean {
  for (const name of classChain) {
    if (isOverlayClass(name)) return false;
    if (name === FLIGHT_VIEWPORT_CLASS) return true;
  }
  return false;
}

/** Class names from an event target outward to the document root, nearest first — the input `isViewportPointerTarget` reads. */
export function classChainOf(target: EventTarget | null): string[] {
  const chain: string[] = [];
  let node = target instanceof Element ? target : null;
  while (node) {
    for (const name of node.classList) chain.push(name);
    node = node.parentElement;
  }
  return chain;
}

/**
 * Where the three control axes are being *asked* to go this frame: −1, 0 or +1
 * per axis, straight off the held keys.
 *
 * This is a **demand**, not a stick position, and the difference is AD-007n
 * change 4. `RB.EXE`'s keyboard is a ramped analogue axis — held, the aileron
 * takes about a second and a half to reach full deflection; released, it keeps
 * rolling for another second — and the port's was a switch, so every input was a
 * full-deflection input. `packages/engine`'s `advanceStick` turns this demand
 * into the position the sim actually flies, at whichever shaping the player has
 * chosen (`stick-settings.ts`), and `flight-scene.ts` carries the position
 * across frames the way it already carries the throttle lever.
 */
export function stickDemandFrom(pressed: ReadonlySet<string>): StickAxes {
  return {
    pitch: axisValue(pressed, PITCH),
    roll: axisValue(pressed, ROLL),
    yaw: axisValue(pressed, YAW),
  };
}

/** Builds one tick's sim inputs from the shaped stick plus the throttle lever's current position; `mouseFiring` is the held primary button. */
export function flightInputsFrom(
  pressed: ReadonlySet<string>,
  stick: StickAxes,
  throttle: number,
  mouseFiring = false,
): FlightInputs {
  return {
    ...NEUTRAL_INPUTS,
    pitch: clamp(stick.pitch, -1, 1),
    roll: clamp(stick.roll, -1, 1),
    yaw: clamp(stick.yaw, -1, 1),
    throttle: clamp(throttle, 0, 1),
    firing: mouseFiring || FIRE_KEYS.some((key) => pressed.has(key)),
    clearJam: pressed.has(CLEAR_JAM_KEY),
    // Inert on an aeroplane with an empty rack, which is every aeroplane the
    // original ever flew and every one here unless a mission handed the player
    // bombs — the engine's bomb pass ignores a rack with nothing on it.
    bombRelease: pressed.has(BOMB_RELEASE_KEY),
  };
}
